import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, HeartHandshake, TrendingUp, ArrowRight, MapPin, Star } from "lucide-react";
import { agent, images, stats } from "../mock";
import { Button } from "../components/ui/button";
import VACalculator from "../components/VACalculator";
import ContactForm from "../components/ContactForm";
import { PropertySearch, Testimonials } from "../components/Sections";
import { LenderCard } from "../components/Lender";

const Home = () => {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={images.heroExteriors[0]}
            alt="Happy Texas family with a SOLD sign in front of their new home"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay-right" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-8 w-full py-24">
          <div className="max-w-xl md:ml-auto md:text-right">
            <div className="fade-up inline-flex items-center gap-2 bg-[#d4af37]/90 text-white text-[11px] tracking-luxury uppercase px-4 py-2 rounded-full mb-7">
              <ShieldCheck className="w-3.5 h-3.5" /> No Minimum Credit Score for VA Loans*
            </div>
            <h1 className="fade-up fade-up-delay-1 font-display text-4xl md:text-6xl leading-[1.1] text-white mb-6">
              Experience You Can Trust. <br /> Service You'll Remember.
            </h1>
            <p className="fade-up fade-up-delay-2 text-lg text-[#efe6d6] leading-relaxed mb-9 max-w-xl">
              {agent.subtagline}
            </p>
            <div className="fade-up fade-up-delay-3 flex flex-col sm:flex-row gap-4 md:justify-end">
              <Link to="/apply-to-buy">
                <Button className="bg-[#d4af37] hover:bg-[#c19a2e] text-white rounded-full px-8 h-13 py-6 tracking-luxury uppercase text-xs w-full sm:w-auto">
                  Start Your Journey <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/credit-challenges">
                <Button
                  variant="outline"
                  className="border-white/70 text-black hover:bg-white hover:text-[#141210] bg-white/90 rounded-full px-8 h-13 py-6 tracking-luxury uppercase text-xs w-full sm:w-auto"
                >
                  Been Told "No"?
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROPERTY SEARCH — front and center */}
      <section className="bg-[#f4f1ea]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs tracking-luxury uppercase text-gold">Start Your Home Search</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
              Search Available Properties
            </h2>
            <p className="text-[#5c5348] mt-4 flex items-center justify-center gap-2">
              <MapPin className="w-4 h-4 text-gold" /> {agent.location}
            </p>
          </div>
          <PropertySearch />
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#141210]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-4xl md:text-5xl text-[#f4ebd9]">{s.value}</div>
              <div className="text-[11px] md:text-xs tracking-luxury uppercase text-[#a8967a] mt-2">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO / SPECIALTY */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="img-zoom rounded-3xl overflow-hidden shadow-[0_30px_70px_-40px_rgba(84,64,34,0.5)]">
            <img
              src="/celebrating-couple.jpg"
              alt="Excited couple celebrating great news about their home"
              className="w-full h-[520px] object-cover"
            />
          </div>
          <div>
            <span className="text-xs tracking-luxury uppercase text-gold">My Specialty</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4 mb-6 leading-tight">
              The agent for buyers who were told they couldn't.
            </h2>
            <p className="text-[#5c5348] leading-relaxed mb-6">
              I'm Kristy Russo, a top-ranking sales and listing agent with RE/MAX Corridor. My heart
              — and my expertise — lives with two groups of people: <strong>veterans using their
              VA benefits</strong> and <strong>buyers who've faced credit challenges</strong>.
            </p>
            <p className="text-[#5c5348] leading-relaxed mb-8">
              Because I partner with a genuinely flexible lender, I can often say "yes" when the big
              banks say "no." No shame, no lectures — just a warm, casual-luxury experience and a
              real plan to get you home.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { icon: ShieldCheck, label: "VA Loan Expert" },
                { icon: HeartHandshake, label: "Credit-Friendly" },
                { icon: TrendingUp, label: "Top Corridor Agent" },
              ].map((f) => (
                <div key={f.label} className="flex flex-col items-start gap-3">
                  <div className="w-11 h-11 rounded-full bg-[#f7edcf] flex items-center justify-center">
                    <f.icon className="w-5 h-5 text-gold" />
                  </div>
                  <span className="text-sm font-medium text-[#141210]">{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROPERTY SEARCH (moved directly under hero) */}

      {/* CALCULATOR */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs tracking-luxury uppercase text-gold">Plan with Confidence</span>
          <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
            What could your payment look like?
          </h2>
        </div>
        <VACalculator />
      </section>

      {/* PREFERRED LENDER */}
      <section className="bg-[#f4f1ea]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs tracking-luxury uppercase text-gold">Financing, Made Easy</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
              My preferred lending partner
            </h2>
          </div>
          <LenderCard dark />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="flex justify-center gap-1 mb-4">
              {["s1", "s2", "s3", "s4", "s5"].map((sid) => (
                <Star key={sid} className="w-5 h-5 fill-[#d4af37] text-[#d4af37]" />
              ))}
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210]">
              Homes closed. Hearts full.
            </h2>
          </div>
          <Testimonials />
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">
          <div className="lg:col-span-2">
            <span className="text-xs tracking-luxury uppercase text-gold">Contact Me</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4 mb-6">
              I'm happy to help!
            </h2>
            <p className="text-[#5c5348] leading-relaxed mb-8">
              Whether you're buying, selling, or just wondering if you'd qualify — send a note.
              I personally read and reply to every message.
            </p>
            <div className="space-y-4 text-[#3a332c]">
              <a href={agent.phoneHref} className="block hover:text-gold transition-colors">
                <span className="text-xs tracking-luxury uppercase text-gold block mb-1">Call or Text</span>
                {agent.phone}
              </a>
              <a href={agent.emailHref} className="block hover:text-gold transition-colors break-all">
                <span className="text-xs tracking-luxury uppercase text-gold block mb-1">Email</span>
                {agent.email}
              </a>
            </div>
          </div>
          <div className="lg:col-span-3 bg-white rounded-3xl p-8 md:p-10 shadow-[0_30px_70px_-45px_rgba(84,64,34,0.5)] border border-[#efe6d6]">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
